import CartAPI from "./CartApi";

/**
 * Interface for reward configuration from DOM
 */
interface RewardConfig {
    goal1: number;
    goal2: number | null;
    rewardVariantId1: string | null;
    rewardVariantId2: string | null;
}

/**
 * Simple cache for cart data
 */
interface CartCache {
    data: any;
    timestamp: number;
    ttl: number; // Time to live in milliseconds
}

/**
 * RewardManager - Handles all reward product logic
 * Centralized management for adding/removing reward products based on cart milestones
 * OPTIMIZED VERSION with caching and minimal HTTP calls
 */
class RewardManager {
    private cartAPI;
    private cartCache: CartCache | null = null;
    private readonly CACHE_TTL = 2000; // 2 seconds cache TTL

    constructor() {
        this.cartAPI = CartAPI;
    }

    /**
     * Clear the cart cache
     */
    clearCache(): void {
        this.cartCache = null;
    }

    /**
     * Get cached cart data or fetch fresh data
     * @param forceRefresh - Force a fresh fetch even if cache is valid
     */
    private async getCachedCartData(forceRefresh: boolean = false): Promise<any> {
        const now = Date.now();
        
        // Use cache if it's valid and not forcing refresh
        if (!forceRefresh && this.cartCache && (now - this.cartCache.timestamp) < this.cartCache.ttl) {
            return this.cartCache.data;
        }
        
        // Fetch fresh data
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Error fetching cart data');
            
            const data = await response.json();
            
            // Update cache
            this.cartCache = {
                data,
                timestamp: now,
                ttl: this.CACHE_TTL
            };
            
            return data;
        } catch (error) {
            console.error('Error fetching cart data:', error);
            // Return cached data if available, even if expired
            return this.cartCache?.data || null;
        }
    }

    /**
     * Get reward configuration from DOM data-attributes
     * Reads from .free-shipping-bar element
     */
    getRewardConfig(): RewardConfig | null {
        const bar = document.querySelector('.free-shipping-bar') as HTMLElement;
        
        if (!bar) {
            return null;
        }

        const config: RewardConfig = {
            goal1: parseInt(bar.dataset.goalOne || '0'),
            goal2: bar.dataset.goalTwo ? parseInt(bar.dataset.goalTwo) : null,
            rewardVariantId1: bar.dataset.rewardProductOne || null,
            rewardVariantId2: bar.dataset.rewardProductTwo || null
        };

        return config;
    }

    /**
     * Check if a specific reward product is in the cart (using cart data)
     * @param cartData - Pre-fetched cart data
     * @param variantId - The variant ID to check
     * @returns true if the reward is in cart
     */
    isRewardInCartFromData(cartData: any, variantId: string): boolean {
        if (!cartData || !cartData.items) return false;
        
        return cartData.items.some((item: any) => {
            return item.variant_id?.toString() === variantId && 
                   item.properties && 
                   item.properties._reward_product === 'true';
        });
    }

    /**
     * Get the count of regular items (excluding reward products) from cart data
     * @param cartData - Pre-fetched cart data
     * @returns number of regular items in cart
     */
    getRegularItemsCountFromData(cartData: any): number {
        if (!cartData || !cartData.items) return 0;
        
        return cartData.items.reduce((acc: number, item: any) => {
            const isReward = item.properties && item.properties._reward_product === 'true';
            if (!isReward) {
                return acc + item.quantity;
            }
            return acc;
        }, 0);
    }

    /**
     * Check if a specific reward product is in the cart
     * @param variantId - The variant ID to check
     * @returns true if the reward is in cart
     */
    async isRewardInCart(variantId: string): Promise<boolean> {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Error fetching cart data');

            const cart = await response.json();
            
            const found = cart.items.some((item: any) => {
                return item.variant_id?.toString() === variantId && 
                       item.properties && 
                       item.properties._reward_product === 'true';
            });

            return found;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get the count of regular items (excluding reward products)
     * @returns number of regular items in cart
     */
    async getRegularItemsCount(): Promise<number> {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Error fetching cart data');

            const cart = await response.json();
            
            const regularCount = cart.items.reduce((acc: number, item: any) => {
                const isReward = item.properties && item.properties._reward_product === 'true';
                if (!isReward) {
                    return acc + item.quantity;
                }
                return acc;
            }, 0);

            return regularCount;
        } catch (error) {
            return 0;
        }
    }

    /**
     * Add a reward product to the cart
     * OPTIMIZED: Check if already exists using cart data parameter
     * @param variantId - The variant ID to add
     * @param cartData - Optional pre-fetched cart data to avoid HTTP call
     */
    async addRewardProduct(variantId: string, cartData?: any): Promise<void> {
        try {
            let alreadyExists = false;
            
            if (cartData) {
                // Use provided cart data to check if reward already exists
                alreadyExists = this.isRewardInCartFromData(cartData, variantId);
            } else {
                // Fallback to HTTP call if no cart data provided
                alreadyExists = await this.isRewardInCart(variantId);
            }
            
            if (alreadyExists) {
                return;
            }
            
            await this.cartAPI.addToCart({
                items: [
                    {
                        id: Number(variantId),
                        quantity: 1,
                        properties: { _reward_product: 'true' }
                    }
                ]
            });

        } catch (error) {
            throw error;
        }
    }

    /**
     * Remove a reward product from the cart
     * OPTIMIZED: Use provided cart data when available
     * @param variantId - The variant ID to remove
     * @param cartData - Optional pre-fetched cart data to avoid HTTP call
     */
    async removeRewardProduct(variantId: string, cartData?: any): Promise<void> {
        try {
            let rewardItem = null;
            
            if (cartData && cartData.items) {
                // Use provided cart data
                rewardItem = cartData.items.find((item: any) => {
                    return item.variant_id?.toString() === variantId && 
                           item.properties && 
                           item.properties._reward_product === 'true';
                });
            } else {
                // Fallback to HTTP call
                const response = await fetch('/cart.js');
                if (!response.ok) throw new Error('Error fetching cart data');
                const cart = await response.json();
                
                rewardItem = cart.items.find((item: any) => {
                    return item.variant_id?.toString() === variantId && 
                           item.properties && 
                           item.properties._reward_product === 'true';
                });
            }

            if (!rewardItem) {
                return;
            }
            
            await this.cartAPI.updateItemCart({
                id: rewardItem.key,
                quantity: 0
            });

        } catch (error) {
            throw error;
        }
    }

    /**
     * Ensure reward products have maximum quantity of 1
     * If any reward has quantity > 1, it will be corrected to 1
     * @returns true if any corrections were made
     */
    async enforceRewardQuantityLimit(): Promise<boolean> {
        try {
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Error fetching cart data');

            const cart = await response.json();
            let correctionsMade = false;

            const rewardsToCorrect = cart.items.filter((item: any) => {
                const isReward = item.properties && item.properties._reward_product === 'true';
                return isReward && item.quantity > 1;
            });

            for (const item of rewardsToCorrect) {
                await this.cartAPI.updateItemCart({
                    id: item.key,
                    quantity: '1'
                });

                correctionsMade = true;
            }

            return correctionsMade;
        } catch (error) {
            return false;
        }
    }

    /**
     * Main function: Evaluate cart and update rewards based on goals
     * ULTRA-OPTIMIZED VERSION - Uses single cart data fetch for all operations
     * This is the central logic that decides which rewards to add/remove
     * @returns true if any changes were made to the cart
     */
    async evaluateAndUpdateRewards(): Promise<boolean> {
        const config = this.getRewardConfig();
        if (!config) {
            return false;
        }

        let changesMade = false;

        try {
            // Single cart data fetch instead of multiple calls
            const cartData = await this.getCachedCartData();
            if (!cartData) {
                return false;
            }

            // Check and correct reward quantities using existing cart data
            const rewardsToCorrect = cartData.items.filter((item: any) => {
                const isReward = item.properties && item.properties._reward_product === 'true';
                return isReward && item.quantity > 1;
            });

            // If we need to make corrections, clear cache since cart will change
            if (rewardsToCorrect.length > 0) {
                for (const item of rewardsToCorrect) {
                    await this.cartAPI.updateItemCart({
                        id: item.key,
                        quantity: '1'
                    });
                    changesMade = true;
                }
                
                // Clear cache after making changes
                this.clearCache();
            }

            // Get regular items count from existing cart data
            const regularCount = this.getRegularItemsCountFromData(cartData);

            // Check reward 1
            if (config.rewardVariantId1 && config.goal1 > 0) {
                const isReward1InCart = this.isRewardInCartFromData(cartData, config.rewardVariantId1);
                const shouldHaveReward1 = regularCount >= config.goal1;

                if (shouldHaveReward1 && !isReward1InCart) {
                    await this.addRewardProduct(config.rewardVariantId1, cartData);
                    changesMade = true;
                    this.clearCache(); // Clear cache after cart change
                } else if (!shouldHaveReward1 && isReward1InCart) {
                    await this.removeRewardProduct(config.rewardVariantId1, cartData);
                    changesMade = true;
                    this.clearCache(); // Clear cache after cart change
                }
            }

            // Check reward 2
            if (config.rewardVariantId2 && config.goal2 && config.goal2 > 0) {
                const isReward2InCart = this.isRewardInCartFromData(cartData, config.rewardVariantId2);
                const shouldHaveReward2 = regularCount >= config.goal2;

                if (shouldHaveReward2 && !isReward2InCart) {
                    await this.addRewardProduct(config.rewardVariantId2, cartData);
                    changesMade = true;
                    this.clearCache(); // Clear cache after cart change
                } else if (!shouldHaveReward2 && isReward2InCart) {
                    await this.removeRewardProduct(config.rewardVariantId2, cartData);
                    changesMade = true;
                    this.clearCache(); // Clear cache after cart change
                }
            }

            return changesMade;

        } catch (error) {
            console.error('Error in evaluateAndUpdateRewards:', error);
            this.clearCache(); // Clear cache on error
            return false;
        }
    }

    /**
     * LEGACY VERSION - Kept for compatibility but not recommended
     * Main function: Evaluate cart and update rewards based on goals
     * This version makes multiple HTTP calls and is slower
     * @returns true if any changes were made to the cart
     */
    async evaluateAndUpdateRewardsLegacy(): Promise<boolean> {
        const config = this.getRewardConfig();
        if (!config) {
            return false;
        }

        let changesMade = false;

        try {
            const quantityCorrected = await this.enforceRewardQuantityLimit();
            if (quantityCorrected) {
                changesMade = true;
            }

            const regularCount = await this.getRegularItemsCount();

            if (config.rewardVariantId1 && config.goal1 > 0) {
                const isReward1InCart = await this.isRewardInCart(config.rewardVariantId1);
                const shouldHaveReward1 = regularCount >= config.goal1;

                if (shouldHaveReward1 && !isReward1InCart) {
                    await this.addRewardProduct(config.rewardVariantId1);
                    changesMade = true;
                } else if (!shouldHaveReward1 && isReward1InCart) {
                    await this.removeRewardProduct(config.rewardVariantId1);
                    changesMade = true;
                }
            }

            if (config.rewardVariantId2 && config.goal2 && config.goal2 > 0) {
                const isReward2InCart = await this.isRewardInCart(config.rewardVariantId2);
                const shouldHaveReward2 = regularCount >= config.goal2;

                if (shouldHaveReward2 && !isReward2InCart) {
                    await this.addRewardProduct(config.rewardVariantId2);
                    changesMade = true;
                } else if (!shouldHaveReward2 && isReward2InCart) {
                    await this.removeRewardProduct(config.rewardVariantId2);
                    changesMade = true;
                }
            }

            return changesMade;

        } catch (error) {
            return false;
        }
    }
}

// Export a singleton instance
const rewardManager = new RewardManager();
export default rewardManager;
